import dayjs from "dayjs";
import { Check, X } from "lucide-react";
import type { Variants } from "motion/react";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useModalStore } from "store/modal/hooks/useModalStore";
import { formatTime, ScrollToElement } from "utils";
import { type BidPostData, getLenderResponseDefaultValues } from "validations";

import type { BadgeProps } from "@krea/shared/design-system/Badge";
import type { FileMetadata } from "@krea/shared/hooks/useFileUpload";
import { cn } from "@krea/shared/utils";
import {
  formatLenderMonthlyInterestRate,
  type LenderMonthlyInterestUseCase,
} from "@krea/shared/utils/formatNumber";

import {
  type BidTypeEnum,
  FieldRequestTypeEnum,
} from "@/features/common/enums";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useSaveBid } from "@/features/lender-responses/hooks/data/use-save-bid.mutation";
import type { IFieldRequest } from "@/features/lender-responses/types/field-request";
import type {
  ICompanyLoanBid,
  ICompanyLoanBidInput,
  IFactoringBid,
  IFactoringBidInput,
  IFieldRequestInput,
  ILeasingBid,
  ILeasingBidInput,
  ILenderResponse,
  IPromotionInput,
  IRealEstateBid,
  IRealEstateBidInput,
  IRevolvingCreditBid,
  IRevolvingCreditBidInput,
  UnionBidInput,
} from "@/features/lender-responses/types/lender-response";

import { useLenderApplicationDetailsStore } from "../lenderApplicationDetailsStore";

export const EmptyField = ({ className }: ComponentProps<"div">) => {
  return (
    <div className={cn(className, "text-gray-700")}>
      <X className="size-4" />
    </div>
  );
};

interface IBidDetailsListItemProps extends ComponentProps<"div"> {
  title: string;
  value: ReactNode;
  /** "summary" = bid summary (table-cell style); "details" = expandable details list */
  variant?: "summary" | "details";
}

export const BidDetailsListItem = ({
  title,
  value,
  className,
  variant = "details",
}: IBidDetailsListItemProps) => {
  if (variant === "details") {
    return (
      <dl
        className={cn(
          "m-0 my-4 overflow-hidden p-0 last:mb-0",
          className,
        )}
      >
        <dt className="text-sm font-normal text-gray-600">{title}</dt>
        <dd className="m-0 text-base font-bold text-black">
          {value}
        </dd>
      </dl>
    );
  }

  return (
    <div
      className={cn(
        "flex py-1.5 text-base gap-x-4 justify-between items-center w-full my-auto mr-0 xl:mr-2 xl:py-2 xl:w-auto xl:min-w-[150px] xl:flex-col xl:items-start xl:justify-start xl:gap-x-0",
        className,
      )}
    >
      <p className="m-0 shrink-0 text-base text-gray-600">
        {title}
      </p>
      <p className="m-0 text-right text-base font-bold xl:text-left">
        {value}
      </p>
    </div>
  );
};

export const CheckedField = ({ className }: ComponentProps<"div">) => {
  return (
    <div className={cn(className, "text-green-600")}>
      <Check className="size-4" />
    </div>
  );
};

export const bidFramerVariants: Variants = {
  initial: {
    opacity: 0,
    height: 0,
  },
  visible: (custom) => ({
    opacity: 1,
    height: "auto",
    transition: {
      delay: custom * 0.05,
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  }),
  hidden: (custom) => ({
    opacity: 0,
    height: 0,
    transition: {
      delay: custom * 0.05,
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  }),
};

interface IBidPreviewProps {
  preview: true;
  lenderApplication: ILenderApplicationExtended;
  lenderResponse: {
    bids: UnionBidInput[];
    fieldRequests: IFieldRequestInput[];
    onboardingStartedAt?: undefined;
    creditAssessmentDoneAt?: undefined;
    signingLinkClickedAt?: undefined;
    promotions?: IPromotionInput[];
    // id: number;
  };
}

interface IBidNoPreviewProps {
  preview: false;
  lenderApplication: ILenderApplicationExtended;
  lenderResponse: ILenderResponse;
}

export type IBidProps = IBidPreviewProps | IBidNoPreviewProps;

interface IUserBidCommonPropertiesReturnValue {
  additionalInformationFieldRequests: (IFieldRequestInput | IFieldRequest)[];
  collateralFieldRequests: (IFieldRequestInput | IFieldRequest)[];
  dateCreated: string;
  dateUpdated: string;
  isUpdated: boolean;
  isOpen: boolean;
  readMoreText: string;
  showMore: () => void;
  statusText: string;
  statusVariant: BadgeProps["variant"];
  onDelete: () => void;
  isSelectedLenderResponse: boolean;
}

export const useLenderResponseCommonProperties = ({
  preview,
  lenderResponse,
  lenderApplication,
}: Pick<
  IBidProps,
  "preview" | "lenderResponse" | "lenderApplication"
>): IUserBidCommonPropertiesReturnValue => {
  const { loanApplication } = lenderApplication;
  const showDeleteBidModal = useModalStore((state) => state.showDeleteBidModal);
  const [isOpen, setIsOpen] = useState(false);

  const { fieldRequests } = lenderResponse;
  const { lenderCompanyId } = useParams<{ lenderCompanyId: string }>();

  const isPreview = preview || !("id" in lenderResponse);

  const showMore = () => {
    setIsOpen(!isOpen);
  };
  const readMoreText = isOpen ? "Collapse" : "Show more";

  if (isPreview) {
    return {
      additionalInformationFieldRequests: fieldRequests.filter((fr) => {
        if (fr.type === FieldRequestTypeEnum.ADDITIONAL_INFORMATION) {
          if (("checked" in fr && fr.checked) || !("checked" in fr)) {
            return true;
          }
        }

        return false;
      }),
      collateralFieldRequests: fieldRequests.filter((fr) => {
        if (fr.type === FieldRequestTypeEnum.COLLATERAL) {
          if (("checked" in fr && fr.checked) || !("checked" in fr)) {
            return true;
          }
        }

        return false;
      }),
      dateCreated: "",
      dateUpdated: "",
      isUpdated: false,
      isOpen,
      showMore,
      readMoreText,
      statusText: "",
      statusVariant: undefined,
      onDelete: () => {},
      isSelectedLenderResponse: false,
    } as const;
  }
  const dateCreated = formatTime(lenderResponse.createdAt);
  const dateUpdated = formatTime(lenderResponse.updatedAt);

  const selectedLenderResponseId = loanApplication.selectedLenderResponse?.id;
  const isSelectedLenderResponse =
    !!selectedLenderResponseId &&
    selectedLenderResponseId === lenderResponse.id;

  let statusText = lenderResponse.isActive ? "Active" : "Inactive";
  let statusVariant: BadgeProps["variant"] = lenderResponse.isActive
    ? "success"
    : "destructive";

  if (isSelectedLenderResponse) {
    statusText = "Selected";
    statusVariant = "default";

    if (lenderResponse.paidOutAt) {
      statusText = "Paid out";
      statusVariant = "success";
    }
  }

  // If it's bid preview, only show a field request if it is checked
  // If it is not bid preview, show it if it exists in lender_response
  const collateralFieldRequests = fieldRequests.filter(
    ({ type }) => type === FieldRequestTypeEnum.COLLATERAL,
  );

  const additionalInformationFieldRequests = fieldRequests.filter(
    ({ type }) => type === FieldRequestTypeEnum.ADDITIONAL_INFORMATION,
  );

  const isUpdated = dayjs(lenderResponse.updatedAt).isAfter(
    dayjs(lenderResponse.createdAt),
  );

  const onDelete = () => {
    showDeleteBidModal({
      lenderApplicationId: lenderApplication.id,
      lenderResponseId: lenderResponse.id,
      lenderCompanyId: Number(lenderCompanyId),
    });
  };

  return {
    additionalInformationFieldRequests,
    collateralFieldRequests,
    dateCreated,
    dateUpdated,
    isUpdated,
    isOpen,
    readMoreText,
    showMore,
    statusVariant,
    statusText,
    onDelete,
    isSelectedLenderResponse,
  };
};

export type DynamicLenderResponse =
  | {
      bid: ICompanyLoanBid | ICompanyLoanBidInput;
      bidType: BidTypeEnum.COMPANY_LOAN;
    }
  | {
      bid: IFactoringBid | IFactoringBidInput;
      bidType: BidTypeEnum.FACTORING;
    }
  | {
      bid: IRealEstateBid | IRealEstateBidInput;
      bidType: BidTypeEnum.REAL_ESTATE;
    }
  | {
      bid: ILeasingBid | ILeasingBidInput;
      bidType: BidTypeEnum.LEASING;
    }
  | {
      bid: IRevolvingCreditBid | IRevolvingCreditBidInput;
      bidType: BidTypeEnum.REVOLVING_CREDIT;
    };

interface IUseManageLenderResponseProps {
  bidType: BidTypeEnum;
  lenderApplication: ILenderApplicationExtended;
}

export const useManageLenderResponse = ({
  bidType,
  lenderApplication,
}: IUseManageLenderResponseProps) => {
  const { reset, handleSubmit } = useFormContext<BidPostData>();
  const {
    currentlyUpdatingLenderResponseId,
    setCurrentlyUpdatingLenderResponseId,
  } = useLenderApplicationDetailsStore(
    ({
      currentlyUpdatingLenderResponseId,
      setCurrentlyUpdatingLenderResponseId,
    }) => ({
      currentlyUpdatingLenderResponseId,
      setCurrentlyUpdatingLenderResponseId,
    }),
  );
  const { mutateAsync: placeBid } = useSaveBid();
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const resetLenderResponse = useCallback(() => {
    setCurrentlyUpdatingLenderResponseId(null);
    const initialValues = getLenderResponseDefaultValues(
      bidType,
      lenderApplication.loanApplication,
    );
    reset(initialValues);
  }, [
    bidType,
    reset,
    lenderApplication.loanApplication,
    setCurrentlyUpdatingLenderResponseId,
  ]);

  const handleLenderResponseSubmit = useCallback(() => {
    if (!lenderApplication.id) return;
    handleSubmit(
      async (data) => {
        await placeBid(
          {
            input: data,
            lenderCompanyId: Number(lenderCompany?.id),
            lenderApplicationId: lenderApplication.id,
            lenderResponseId: currentlyUpdatingLenderResponseId
              ? currentlyUpdatingLenderResponseId
              : undefined,
          },
          {
            onSuccess: (response) => {
              const sortedLenderResponses = response.lenderResponses
                ?.filter((it) => it.isActive)
                .sort(
                  (a, b) =>
                    new Date(b.updatedAt).getTime() -
                    new Date(a.updatedAt).getTime(),
                );

              if (sortedLenderResponses && sortedLenderResponses.length > 0) {
                const createdLenderResponse = sortedLenderResponses[0];
                setTimeout(() => {
                  ScrollToElement(
                    `scrollToLenderResponseId-${createdLenderResponse?.id}`,
                  );

                  // Reset the bid form after the scroll
                  // Also, wierd react-hook-form bug where the form is not resetting promotions to empty list correct otherwise
                  // By doing it after the timeout and scroll, it works, but strange that we need to do this.
                  resetLenderResponse();
                }, 200);

                const totalInterestRate = createdLenderResponse?.bids?.reduce(
                  (acc, bid) => {
                    return acc + bid.comparableMonthlyInterestRate;
                  },
                  0,
                );

                let avgInterestRate: number | null = null;

                if (
                  createdLenderResponse?.bids?.length &&
                  createdLenderResponse.bids.length > 0 &&
                  totalInterestRate !== undefined
                ) {
                  avgInterestRate =
                    totalInterestRate / createdLenderResponse.bids.length;
                }

                if (avgInterestRate && avgInterestRate > 10.0) {
                  toast.warning(
                    "Comparable interest seems high - please verify input",
                  );
                }
              }
            },
          },
        );
      },
      (e) => console.error(e),
    )();
  }, [
    currentlyUpdatingLenderResponseId,
    handleSubmit,
    lenderApplication.id,
    lenderCompany?.id,
    placeBid,
    resetLenderResponse,
  ]);

  return {
    resetLenderResponse,
    handleLenderResponseSubmit,
  } as const;
};

export const formatLenderResponseFormData = (
  data: BidPostData,
  useCase: LenderMonthlyInterestUseCase,
  isFeatureInterestRatePerYearActivated?: boolean, // If a lender company has the feature activated, we need to convert the "monthly interest rate" data point.
): BidPostData => {
  // Convert interest rate per year to interest rate per 30 days if the feature is activated

  if (isFeatureInterestRatePerYearActivated) {
    data.bids = data.bids.map((bid) => {
      if ("lenderMonthlyInterest" in bid && bid.lenderMonthlyInterest) {
        return {
          ...bid,
          lenderMonthlyInterest: formatLenderMonthlyInterestRate(
            bid.lenderMonthlyInterest,
            useCase,
            isFeatureInterestRatePerYearActivated,
          ),
        };
      }

      return bid;
    });
  }

  return data;
};

export const getFormValuesFromLenderResponse = (
  lenderResponse: ILenderResponse,
  isFeatureInterestRatePerYearActivated?: boolean,
): BidPostData => {
  const { bids, promotions, fieldRequests, creditTermsFile } = lenderResponse;

  let existingCreditTermsFile: FileMetadata | null = null;

  if (creditTermsFile) {
    existingCreditTermsFile = {
      id: creditTermsFile.id.toString(),
      name: creditTermsFile.originalFileName ?? "",
      size: 0,
      type: "",
      url: creditTermsFile.fileUrl ?? "",
    };
  }

  return formatLenderResponseFormData(
    {
      bids,
      fieldRequests,
      promotions,
      creditTermsFile: existingCreditTermsFile,
    },
    "TO_YEARLY",
    isFeatureInterestRatePerYearActivated,
  );
};
